import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

const FLOOR_TYPES = [
  'PVC klik',
  'PVC verlijmd',
  'Visgraat PVC',
  'Hongaarse punt',
  'Parket',
  'Anders',
];

const CITY_LOOKUP: Record<string, string> = {
  '1011': 'Amsterdam',
  '3511': 'Utrecht',
  '3011': 'Rotterdam',
  '5611': 'Eindhoven',
  '5041': 'Tilburg',
  '2311': 'Leiden',
  '8911': 'Leeuwarden',
};

function normalizePostcode(value: string) {
  return value.toUpperCase().replace(/\s+/g, '');
}

function lookupCity(postcode: string) {
  const cleaned = normalizePostcode(postcode);
  const prefix = cleaned.slice(0, 4);
  return CITY_LOOKUP[prefix] || null;
}

function validateDutchPhone(phone: string) {
  const cleaned = phone.replace(/[^0-9+]/g, '');
  return /^((\+31|0031)[1-9][0-9]{8}|0[1-9][0-9]{8})$/.test(cleaned);
}

function formatRawPayload(values: Record<string, string | number | null>) {
  return {
    voornaam: values.firstName,
    achternaam: values.lastName,
    telefoon: values.phone,
    email: values.email,
    vloertype: values.floorType,
    oppervlakte: values.surface,
    postcode: values.postcode,
    stad: values.city,
  };
}

export default function LeadLandingPage() {
  const params = useParams<{ partner?: string }>();
  const [searchParams] = useSearchParams();
  const partnerParam = params.partner || searchParams.get('partner') || null;
  const funnelParam = searchParams.get('funnel') || null;
  const utmSource = searchParams.get('utm_source') || null;
  const utmCampaign = searchParams.get('utm_campaign') || null;
  const utmContent = searchParams.get('utm_content') || null;

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    floorType: 'PVC klik',
    surface: '',
    postcode: '',
    city: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);

  const autoCity = useMemo(() => lookupCity(form.postcode), [form.postcode]);

  useEffect(() => {
    if (!form.city && autoCity) {
      setForm((prev) => ({ ...prev, city: autoCity }));
    }
  }, [autoCity, form.city]);

  const handleChange = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage(null);

    if (!form.firstName.trim() || !form.lastName.trim() || !form.phone.trim() || !form.email.trim() || !form.postcode.trim() || !form.city.trim()) {
      setMessage('Vul alle verplichte velden in.');
      setStatus('error');
      return;
    }

    if (!validateDutchPhone(form.phone.trim())) {
      setMessage('Voer een geldig Nederlands telefoonnummer in.');
      setStatus('error');
      return;
    }

    setStatus('submitting');

    if (!partnerParam) {
      setMessage('Partner ontbreekt. Open deze pagina via de advertentie-link of voeg ?partner=partner-003 toe.');
      setStatus('error');
      return;
    }

    const { data: insertedLead, error } = await supabase
      .from('leads')
      .insert({
        assigned_partner_id: partnerParam,
        funnel_id: funnelParam || null,
        status: 'new',
        routing_status: 'assigned',
        raw_payload: {
          ...formatRawPayload(form),
          bron: partnerParam ? 'partner_landing_page' : 'website',
          utm_source: utmSource,
          utm_campaign: utmCampaign,
          utm_content: utmContent,
        },
      })
      .select('id')
      .single();

    if (error || !insertedLead?.id) {
      setMessage(error?.message || 'Er is iets misgegaan bij het versturen van het formulier.');
      setStatus('error');
      return;
    }

    const { error: fnError } = await supabase.functions.invoke('semmy-lead-intake', {
      body: {
        lead_id: insertedLead.id,
        partner_id: partnerParam,
      },
    });

    if (fnError) {
      console.error('semmy lead intake failed', fnError);
      setMessage('Lead opgeslagen, maar Semmy-intake kon niet worden gestart.');
      setStatus('success');
      return;
    }

    setStatus('success');
  };

  return (
    <div className="min-h-screen bg-[#f7f8fb] px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl rounded-[32px] border border-[#dfe3eb] bg-white p-8 shadow-sm sm:p-10">
        <div className="mb-10 text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#3b82f6]">Leadformulier</p>
          <h1 className="mt-4 text-3xl font-semibold tracking-tight text-[#111827] sm:text-4xl">Vraag een offerte aan voor je vloer</h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-6 text-[#6b7280]">
            Vul je gegevens in en we nemen zo snel mogelijk contact met je op via WhatsApp.
          </p>
        </div>

        {status === 'success' ? (
          <div className="rounded-3xl border border-[#d1d5db] bg-[#f0fdf4] p-8 text-center">
            <h2 className="text-2xl font-semibold text-[#065f46]">Bedankt!</h2>
            <p className="mt-4 text-sm text-[#065f46]">
              Je ontvangt zo snel mogelijk een reactie via WhatsApp.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm font-medium text-[#111827]">
                Voornaam*
                <input
                  type="text"
                  value={form.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  className="mt-2 h-12 w-full rounded-3xl border border-[#d1d5db] bg-[#f9fafb] px-4 text-sm text-[#111827] outline-none transition focus:border-[#2563eb] focus:ring-2 focus:ring-[#bfdbfe]"
                />
              </label>
              <label className="block text-sm font-medium text-[#111827]">
                Achternaam*
                <input
                  type="text"
                  value={form.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  className="mt-2 h-12 w-full rounded-3xl border border-[#d1d5db] bg-[#f9fafb] px-4 text-sm text-[#111827] outline-none transition focus:border-[#2563eb] focus:ring-2 focus:ring-[#bfdbfe]"
                />
              </label>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm font-medium text-[#111827]">
                Telefoonnummer*
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="06 1234 5678"
                  className="mt-2 h-12 w-full rounded-3xl border border-[#d1d5db] bg-[#f9fafb] px-4 text-sm text-[#111827] outline-none transition focus:border-[#2563eb] focus:ring-2 focus:ring-[#bfdbfe]"
                />
              </label>
              <label className="block text-sm font-medium text-[#111827]">
                E-mailadres*
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="naam@voorbeeld.nl"
                  className="mt-2 h-12 w-full rounded-3xl border border-[#d1d5db] bg-[#f9fafb] px-4 text-sm text-[#111827] outline-none transition focus:border-[#2563eb] focus:ring-2 focus:ring-[#bfdbfe]"
                />
              </label>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm font-medium text-[#111827]">
                Vloertype
                <select
                  value={form.floorType}
                  onChange={(e) => handleChange('floorType', e.target.value)}
                  className="mt-2 h-12 w-full rounded-3xl border border-[#d1d5db] bg-[#f9fafb] px-4 text-sm text-[#111827] outline-none transition focus:border-[#2563eb] focus:ring-2 focus:ring-[#bfdbfe]"
                >
                  {FLOOR_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm font-medium text-[#111827]">
                Oppervlakte in m²
                <input
                  type="number"
                  min="1"
                  value={form.surface}
                  onChange={(e) => handleChange('surface', e.target.value)}
                  placeholder="bijv. 45"
                  className="mt-2 h-12 w-full rounded-3xl border border-[#d1d5db] bg-[#f9fafb] px-4 text-sm text-[#111827] outline-none transition focus:border-[#2563eb] focus:ring-2 focus:ring-[#bfdbfe]"
                />
              </label>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm font-medium text-[#111827]">
                Postcode*
                <input
                  type="text"
                  value={form.postcode}
                  onChange={(e) => handleChange('postcode', normalizePostcode(e.target.value))}
                  placeholder="1011AB"
                  className="mt-2 h-12 w-full rounded-3xl border border-[#d1d5db] bg-[#f9fafb] px-4 text-sm text-[#111827] outline-none transition focus:border-[#2563eb] focus:ring-2 focus:ring-[#bfdbfe]"
                />
              </label>
              <label className="block text-sm font-medium text-[#111827]">
                Stad*
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="Amsterdam"
                  className="mt-2 h-12 w-full rounded-3xl border border-[#d1d5db] bg-[#f9fafb] px-4 text-sm text-[#111827] outline-none transition focus:border-[#2563eb] focus:ring-2 focus:ring-[#bfdbfe]"
                />
              </label>
            </div>

            {autoCity && !form.city && (
              <p className="text-sm text-[#4b5563]">Stad automatisch ingevuld: {autoCity}</p>
            )}

            {message && (
              <div className={`rounded-3xl border px-4 py-3 text-sm ${status === 'error' ? 'border-[#fca5a5] bg-[#fef2f2] text-[#b91c1c]' : 'border-[#d1fae5] bg-[#ecfdf5] text-[#166534]'}`}>
                {message}
              </div>
            )}

            <button
              type="submit"
              className="inline-flex w-full items-center justify-center rounded-full bg-[#111827] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#1f2937] disabled:cursor-not-allowed disabled:opacity-60"
              disabled={status === 'submitting'}
            >
              {status === 'submitting' ? 'Versturen…' : 'Offerte aanvragen'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
